import { CalendarDays } from 'lucide-react';
import { fmtMonthYear } from '@siva/shared';
import { t } from '../i18n/index.js';

/**
 * Pick a month, looking and behaving exactly like {@link DateField}.
 *
 * The Reports page used a bare `<input type="month">` here while every other preset used
 * DateField, so choosing Month changed the shape of the control, its colours and its behaviour
 * mid-form. The owner: "compare all day, week, month calender and keep them all in same format".
 *
 * The visible field is a read-only text input rather than a span, so it inherits every bit of
 * input styling the date fields get — border, background, height, focus ring — instead of
 * needing them copied and kept in step. The first attempt used a span and looked like loose text
 * beside a button, which is exactly the inconsistency being fixed.
 *
 * The whole field opens the picker, not just the glyph. A month has nothing worth typing, so the
 * native control is stretched across the entire field rather than tucked behind a 34px icon that
 * is hard to hit on a phone and easy to miss on a desktop. Tapping the month name works because
 * the month name *is* the button.
 */
export function MonthField({
  value,
  max,
  onChange,
  id,
  label,
}: {
  /** 'YYYY-MM-DD'; only the year and month are read. */
  value: string;
  /** 'YYYY-MM-DD'; only the year and month are read. */
  max?: string;
  /** Called with the first day of the chosen month, 'YYYY-MM-DD'. */
  onChange: (firstOfMonth: string) => void;
  id?: string;
  label?: string;
}) {
  // fmtMonthYear takes 'YYYY-MM' and returns its input unchanged for anything else — passing the
  // whole date put "2026-09-01" on screen where "September 2026" belonged.
  const month = value.slice(0, 7);
  return (
    <span className="date-field month-field">
      <input type="text" readOnly tabIndex={-1} value={month ? fmtMonthYear(month) : ''} />
      <span className="date-pick" aria-hidden="true">
        <CalendarDays size={16} />
      </span>
      <input
        type="month"
        className="native"
        id={id}
        aria-label={label ?? t('date_open_calendar')}
        value={month}
        {...(max ? { max: max.slice(0, 7) } : {})}
        onChange={(e) => {
          if (e.target.value) onChange(`${e.target.value}-01`);
        }}
        onClick={(e) => {
          // Chrome opens its picker only from the glyph otherwise; iOS has no showPicker for this
          // and needs none, because the tap has already landed on a real month input.
          try {
            e.currentTarget.showPicker();
          } catch {
            /* the tap itself opens it */
          }
        }}
      />
    </span>
  );
}
